/**
 * TaskAudienceSection — "Dirigir a": elige una audiencia (todos / roles / lotes / cultivos), muestra
 * la VISTA PREVIA de destinatarios (nadie recibe aviso hasta guardar) con casilla para quitar a
 * cualquiera y un buscador para añadir a alguien más del tenant con acceso al rancho. La lista final
 * editada se entrega al padre como `recipients` (foto fija) junto con `audience`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Checkbox, Combobox, Dropdown, Field, Option, Spinner, Text, tokens,
} from '@fluentui/react-components';
import { previewTaskAudience } from '../../api/dashboard.api';
import { fetchCrops } from '../../api/terrain.api';
import { fetchModules } from '../../api/modules.api';
import { loteZoneId, parseLoteZoneId } from '../../utils/lote-zone-options';
import { useLoteZoneOptions } from '../../hooks/useLoteZoneOptions';
import { useT } from '../../i18n';
import type { AudiencePerson, TaskAudience, TaskAudienceType } from '../../types/api.types';

export interface AudienceValue {
  audience: TaskAudience;
  recipients: string[];
}

interface Props {
  /** Lote real, o undefined para tarea general (entonces `ranchoId`). */
  loteId?: string;
  ranchoId?: string;
  lotes: { loteId: string; name: string }[];
  disabled?: boolean;
  /** Edición: audiencia y destinatarios actuales de la tarea. */
  initial?: { audience: TaskAudience; recipients: string[] } | null;
  /** null = tarea individual (sin audiencia). */
  onChange: (value: AudienceValue | null) => void;
}

type Kind = '' | TaskAudienceType;
const ROLE_KEYS = ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR', 'INVESTOR'];

export function TaskAudienceSection({ loteId, ranchoId, lotes, disabled, initial, onChange }: Props) {
  const t = useT();
  const tt = t.tasks;
  const [kind, setKind] = useState<Kind>(initial?.audience.type ?? '');
  const [roles, setRoles] = useState<string[]>(initial?.audience.roles ?? []);
  const [loteIds, setLoteIds] = useState<string[]>(initial?.audience.loteIds ?? []);
  const [moduleIds, setModuleIds] = useState<string[]>(initial?.audience.moduleIds ?? []);
  const [cropIds, setCropIds] = useState<string[]>(initial?.audience.cropIds ?? []);
  // null = "todos los resueltos"; una vez que el usuario quita/añade, es la lista explícita.
  const [selected, setSelected] = useState<Set<string> | null>(initial ? new Set(initial.recipients) : null);
  const [query, setQuery] = useState('');

  const { data: crops = [] } = useQuery({ queryKey: ['crops'], queryFn: fetchCrops, enabled: kind === 'CROPS' });
  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', ranchoId ?? null],
    queryFn: () => fetchModules({ ranchoId }),
    enabled: kind === 'LOTES',
  });
  const zoneOptions = useLoteZoneOptions(lotes, modules);

  const audience: TaskAudience | null = useMemo(() => {
    if (!kind) return null;
    if (kind === 'ALL') return { type: 'ALL' };
    if (kind === 'ROLES') return roles.length ? { type: 'ROLES', roles } : null;
    if (kind === 'LOTES') {
      if (!loteIds.length && !moduleIds.length) return null;
      return {
        type: 'LOTES',
        ...(loteIds.length ? { loteIds } : {}),
        ...(moduleIds.length ? { moduleIds } : {}),
      };
    }
    return cropIds.length ? { type: 'CROPS', cropIds } : null;
  }, [kind, roles, loteIds, moduleIds, cropIds]);

  const scopeOk = !!loteId || !!ranchoId;
  const previewQ = useQuery({
    queryKey: ['task-audience-preview', loteId ?? null, ranchoId ?? null, audience],
    queryFn: () => previewTaskAudience({ audience: audience!, ...(loteId ? { loteId } : { ranchoId }) }),
    enabled: !!audience && scopeOk,
    staleTime: 0,
  });
  const preview = previewQ.data;

  // Al cambiar los criterios de la audiencia la lista editada se descarta (se vuelve a resolver),
  // salvo la primera vez en edicion, donde se conserva la foto fija de la tarea.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setSelected(null);
  }, [kind, roles, loteIds, moduleIds, cropIds, loteId, ranchoId]);

  const resolved = useMemo(() => preview?.recipients ?? [], [preview]);
  const effective = useMemo(
    () => selected ?? new Set(resolved.map(r => r.phoneNumber)),
    [selected, resolved],
  );

  // Filas: los resueltos + los añadidos a mano que no estaban resueltos.
  const rows: AudiencePerson[] = useMemo(() => {
    const byPhone = new Map(resolved.map(r => [r.phoneNumber, r]));
    const extras: AudiencePerson[] = [];
    for (const p of effective) {
      if (byPhone.has(p)) continue;
      const known = preview?.eligible.find(e => e.phoneNumber === p);
      extras.push(known ?? { phoneNumber: p, name: p, role: '', hasTelegram: false, ranchos: [] });
    }
    return [...resolved, ...extras];
  }, [resolved, effective, preview]);

  const addable = useMemo(() => {
    const inRows = new Set(rows.map(r => r.phoneNumber));
    const q = query.trim().toLowerCase();
    return (preview?.eligible ?? [])
      .filter(e => !inRows.has(e.phoneNumber) && (!q || e.name.toLowerCase().includes(q) || e.phoneNumber.includes(q)));
  }, [rows, preview, query]);

  const toggle = (phone: string, on: boolean) => {
    const base = new Set(effective);
    on ? base.add(phone) : base.delete(phone);
    setSelected(base);
  };

  // Entrega el valor al padre (solo cuando hay audiencia valida y la vista previa ya cargo).
  const emitted = useRef('');
  useEffect(() => {
    const value: AudienceValue | null = audience && preview ? { audience, recipients: [...effective] } : null;
    const key = JSON.stringify(value);
    if (key === emitted.current) return;
    emitted.current = key;
    onChange(audience && !preview ? { audience, recipients: [] } : value);
  }, [audience, preview, effective, onChange]);

  const count = effective.size;
  const overCap = !!preview && count > preview.cap;
  const roleName = (r: string) => (tt.audRoleNames as Record<string, string>)[r] ?? r;
  const kindLabel: Record<string, string> = {
    '': tt.audIndividual, ALL: tt.audAll, ROLES: tt.audRoles, LOTES: tt.audLotes, CROPS: tt.audCrops,
  };

  const multi = (
    label: string,
    options: { id: string; label: string }[],
    value: string[],
    set: (v: string[]) => void,
  ) => (
    <Field label={label}>
      <Dropdown
        multiselect
        disabled={disabled}
        selectedOptions={value}
        value={options.filter(o => value.includes(o.id)).map(o => o.label).join(', ')}
        onOptionSelect={(_, d) => set(d.selectedOptions)}
      >
        {options.map(o => <Option key={o.id} value={o.id}>{o.label}</Option>)}
      </Dropdown>
    </Field>
  );

  return (
    <div data-testid="task-audience" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Field label={tt.audTitle}>
        <Dropdown
          disabled={disabled}
          value={kindLabel[kind]}
          selectedOptions={[kind]}
          onOptionSelect={(_, d) => {
            const k = (d.optionValue ?? '') as Kind;
            setKind(k);
            if (!k) onChange(null);
          }}
        >
          {(['', 'ALL', 'ROLES', 'LOTES', 'CROPS'] as Kind[]).map(k => (
            <Option key={k || 'none'} value={k}>{kindLabel[k]}</Option>
          ))}
        </Dropdown>
      </Field>

      {kind === 'ROLES' && multi(tt.audPickRoles, ROLE_KEYS.map(r => ({ id: r, label: roleName(r) })), roles, setRoles)}
      {kind === 'LOTES' && (
        <Field label={tt.audPickLotes}>
          <Dropdown
            multiselect
            disabled={disabled}
            selectedOptions={[...loteIds.map(id => loteZoneId({ loteId: id })), ...moduleIds.map(id => loteZoneId({ loteId: '', moduleId: id }))]}
            value={zoneOptions
              .filter(o => loteIds.some(id => o.id === loteZoneId({ loteId: id })) || moduleIds.some(id => o.id === loteZoneId({ loteId: '', moduleId: id })))
              .map(o => o.label).join(', ')}
            onOptionSelect={(_, d) => {
              const sels = d.selectedOptions.map(id => parseLoteZoneId(id, modules)).filter(s => !!s);
              setLoteIds([...new Set(sels.filter(s => !s.moduleId).map(s => s.loteId))]);
              setModuleIds([...new Set(sels.filter(s => s.moduleId).map(s => s.moduleId as string))]);
            }}
          >
            {zoneOptions.map(o => <Option key={o.id} value={o.id}>{o.label}</Option>)}
          </Dropdown>
        </Field>
      )}
      {kind === 'CROPS' && multi(tt.audPickCrops, crops.map(c => ({ id: c.cropId, label: c.name })), cropIds, setCropIds)}

      {audience && previewQ.isLoading && <Spinner size="tiny" label={tt.audLoading} />}
      {audience && previewQ.isError && (
        <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>{tt.audPreviewError}</Text>
      )}

      {audience && preview && (
        <>
          <Text weight="semibold" size={300} data-testid="audience-count">
            {count === 1 ? tt.audReachOne : tt.audReachCount.replace('{n}', String(count))}
          </Text>
          {count === 0 && (
            <Text size={200} style={{ color: tokens.colorStatusWarningForeground1 }}>{tt.audNoRecipients}</Text>
          )}
          {overCap && (
            <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>
              {tt.audTooMany.replace('{max}', String(preview.cap))}
            </Text>
          )}

          <div style={{ maxHeight: '180px', overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: '6px', padding: '4px 8px' }}>
            {rows.map(p => (
              <div key={p.phoneNumber} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Checkbox
                  disabled={disabled}
                  checked={effective.has(p.phoneNumber)}
                  onChange={(_, d) => toggle(p.phoneNumber, !!d.checked)}
                  aria-label={`${tt.audRemove} ${p.name}`}
                  label={p.name}
                />
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                  {p.role ? roleName(p.role) : ''}{p.ranchos.length ? ` · ${p.ranchos.join(', ')}` : ''}
                  {!p.hasTelegram ? ` · ${tt.audNoTelegram}` : ''}
                </Text>
              </div>
            ))}
          </div>

          <Field label={tt.audAdd}>
            <Combobox
              disabled={disabled}
              placeholder={tt.audAddPlaceholder}
              value={query}
              selectedOptions={[]}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              onOptionSelect={(_, d) => {
                if (d.optionValue) toggle(d.optionValue, true);
                setQuery('');
              }}
            >
              {addable.slice(0, 50).map(p => (
                <Option key={p.phoneNumber} value={p.phoneNumber} text={p.name}>
                  {p.name} — {roleName(p.role)}
                </Option>
              ))}
            </Combobox>
          </Field>
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>{tt.audSnapshotHint}</Text>
        </>
      )}
    </div>
  );
}
